import * as types from "../../Action/actionTypes"

const initialState = {
  // agency view: keyed by campaignId
  byCampaign:       {},   // { [campaignId]: { deliverables: [], stats: {} } }
  loading:          false,
  assigning:        false,
  reviewing:        false,
  submittingAll:    false,

  // creator view
  myDeliverables:     [],
  myDeliverablesLoading: false,
  submittingIds:        [],   // deliverable IDs currently being submitted

  // brand view: keyed by campaignId
  brandByCampaign:  {},
  brandLoading:     false,
  brandReviewing:   false,
}

const upsert = (list, updated) =>
  list.map((d) => d.id === updated.id ? { ...d, ...updated } : d)

const deliverableReducer = (state = initialState, action) => {
  switch (action.type) {

    // ── Agency fetch ───────────────────────────────────────────────────────────
    case types.FETCH_CAMPAIGN_DELIVERABLES_START:
      return { ...state, loading: true }
    case types.FETCH_CAMPAIGN_DELIVERABLES_SUCCESS:
      return {
        ...state, loading: false,
        byCampaign: {
          ...state.byCampaign,
          [action.payload.campaignId]: {
            deliverables: action.payload.deliverables,
            stats:        action.payload.stats,
          },
        },
      }
    case types.FETCH_CAMPAIGN_DELIVERABLES_ERROR:
      return { ...state, loading: false }

    // ── Agency assign ──────────────────────────────────────────────────────────
    case types.ASSIGN_DELIVERABLE_START:
      return { ...state, assigning: true }
    case types.ASSIGN_DELIVERABLE_SUCCESS: {
      const { deliverable, campaignId } = action.payload
      const bucket = state.byCampaign[campaignId] || { deliverables: [], stats: {} }
      const updated = upsert(bucket.deliverables, deliverable)
      return {
        ...state, assigning: false,
        byCampaign: { ...state.byCampaign, [campaignId]: { ...bucket, deliverables: updated } },
      }
    }
    case types.ASSIGN_DELIVERABLE_ERROR:
      return { ...state, assigning: false }

    // ── Agency review ──────────────────────────────────────────────────────────
    case types.REVIEW_DELIVERABLE_START:
      return { ...state, reviewing: true }
    case types.REVIEW_DELIVERABLE_SUCCESS: {
      const { deliverable, campaignId } = action.payload
      const bucket = state.byCampaign[campaignId] || { deliverables: [], stats: {} }
      const updated = upsert(bucket.deliverables, deliverable)
      const stats   = recomputeStats(updated)
      return {
        ...state, reviewing: false,
        byCampaign: { ...state.byCampaign, [campaignId]: { deliverables: updated, stats } },
      }
    }
    case types.REVIEW_DELIVERABLE_ERROR:
      return { ...state, reviewing: false }

    // ── Agency submit all to brand ─────────────────────────────────────────────
    case types.SUBMIT_ALL_TO_BRAND_START:
      return { ...state, submittingAll: true }
    case types.SUBMIT_ALL_TO_BRAND_SUCCESS:
      return {
        ...state, submittingAll: false,
        byCampaign: {
          ...state.byCampaign,
          [action.payload.campaignId]: {
            deliverables: action.payload.deliverables,
            stats:        action.payload.stats,
          },
        },
      }
    case types.SUBMIT_ALL_TO_BRAND_ERROR:
      return { ...state, submittingAll: false }

    // ── Creator fetch ──────────────────────────────────────────────────────────
    case types.FETCH_MY_DELIVERABLES_START:
      return { ...state, myDeliverablesLoading: true }
    case types.FETCH_MY_DELIVERABLES_SUCCESS:
      return { ...state, myDeliverablesLoading: false, myDeliverables: action.payload }
    case types.FETCH_MY_DELIVERABLES_ERROR:
      return { ...state, myDeliverablesLoading: false }

    // ── Creator submit ─────────────────────────────────────────────────────────
    case types.SUBMIT_MY_DELIVERABLE_START:
      return { ...state, submittingIds: [...state.submittingIds, action.payload] }
    case types.SUBMIT_MY_DELIVERABLE_SUCCESS: {
      const updated = action.payload
      return {
        ...state,
        submittingIds:  state.submittingIds.filter((id) => id !== updated.id),
        myDeliverables: state.myDeliverables.map((d) => d.id === updated.id ? { ...d, ...updated } : d),
      }
    }
    case types.SUBMIT_MY_DELIVERABLE_ERROR:
      return { ...state, submittingIds: state.submittingIds.filter((i) => i !== action.payload.id) }

    // ── Brand fetch ────────────────────────────────────────────────────────────
    case types.FETCH_BRAND_DELIVERABLES_START:
      return { ...state, brandLoading: true }
    case types.FETCH_BRAND_DELIVERABLES_SUCCESS:
      return {
        ...state, brandLoading: false,
        brandByCampaign: {
          ...state.brandByCampaign,
          [action.payload.campaignId]: {
            deliverables: action.payload.deliverables,
            stats:        action.payload.stats,
          },
        },
      }
    case types.FETCH_BRAND_DELIVERABLES_ERROR:
      return { ...state, brandLoading: false }

    // ── Brand review ───────────────────────────────────────────────────────────
    case types.BRAND_REVIEW_DELIVERABLE_START:
      return { ...state, brandReviewing: true }
    case types.BRAND_REVIEW_DELIVERABLE_SUCCESS: {
      const { deliverable, campaignId } = action.payload
      const bucket  = state.brandByCampaign[campaignId] || { deliverables: [], stats: {} }
      const updated = upsert(bucket.deliverables, deliverable)
      const stats   = recomputeBrandStats(updated)
      return {
        ...state, brandReviewing: false,
        brandByCampaign: { ...state.brandByCampaign, [campaignId]: { deliverables: updated, stats } },
      }
    }
    case types.BRAND_REVIEW_DELIVERABLE_ERROR:
      return { ...state, brandReviewing: false }

    default:
      return state
  }
}

const recomputeStats = (deliverables) => {
  const total = deliverables.length
  return {
    total,
    not_assigned:    deliverables.filter((d) => d.status === 'not_assigned').length,
    assigned:        deliverables.filter((d) => d.status === 'assigned').length,
    submitted:       deliverables.filter((d) => d.status === 'submitted').length,
    agency_approved: deliverables.filter((d) => d.status === 'agency_approved').length,
    brand_submitted: deliverables.filter((d) => d.status === 'brand_submitted').length,
    brand_approved:  deliverables.filter((d) => d.status === 'brand_approved').length,
    progress:        total > 0 ? Math.round(deliverables.filter((d) => d.status === 'brand_approved').length / total * 100) : 0,
  }
}

const recomputeBrandStats = (deliverables) => {
  const total = deliverables.length
  return {
    total,
    not_assigned:    deliverables.filter((d) => d.status === 'not_assigned').length,
    in_progress:     deliverables.filter((d) => ['assigned','submitted','revision_needed','agency_approved'].includes(d.status)).length,
    brand_submitted: deliverables.filter((d) => d.status === 'brand_submitted').length,
    brand_approved:  deliverables.filter((d) => d.status === 'brand_approved').length,
    progress:        total > 0 ? Math.round(deliverables.filter((d) => d.status === 'brand_approved').length / total * 100) : 0,
  }
}

export default deliverableReducer
